<template>
  <div
    v-if="eventData"
    class="w-full lg:w-[420px] flex flex-col gap-4 mt-8 kft-product__checkout"
  >
    <!-- Placeholder to prevent CLS -->
    <div v-show="!isBuyBoxReady" class="preloader loading-effect"></div>

    <!-- EventBuyBox with v-if to only render when dates are available -->
    <EventBuyBox
      v-if="isBuyBoxReady && (shouldDisplayEventBuybox || isKitsAvailable)"
      :is-embedded="isEmbedded"
      :text-color="textColor"
      :background-color="backgroundColor"
      :card-background-color="cardBackgroundColor"
      :is-kits-page="isKitsPage"
      :enable-giftcard="false"
      :available-dates-quantity="availableDatesQuantity"
      :first-available-date="firstAvailableDate"
      :first-available-month="firstAvailableMonth"
      @dates-loaded="emit('dates-loaded')"
      @pricings-loaded="emit('pricings-loaded')"
      @add-item-to-cart="emit('add-item-to-cart')"
      @toggle-date-picker="emit('toggle-date-picker')"
    />

    <EventGiftcardBuyBoxCard
      v-if="shouldEnableGiftcard"
      :text-color="textColor"
      :enable-embedded="isEmbedded"
      :background-color="backgroundColor"
      :card-background-color="cardBackgroundColor"
      display-price
      :type="eventGetters.getIsTeamEvent(eventData) ? 'PRIVATE' : 'PUBLIC'"
      :should-display-buy-box="shouldDisplayEventBuybox"
      :is-special-giftcard-event="isSpecialGiftcardEvent"
      @add-item-to-cart="emit('add-item-to-cart')"
    />

    <LazyTeameventRequestCard
      v-if="shouldDisplayTeamEventRequestCard"
      :price="teamEventRequestPrice"
    />

    <LazyCorporateGiftingCard
      v-if="shouldDisplayCorporateGiftingCard"
      :event-type="isSpecialGiftcardEvent ? 'GIFT_CARD' : 'BOX'"
    />
  </div>
</template>

<script lang="ts" setup>
import EventBuyBox from './buybox/EventBuyBox.vue'
import type {
  EventCalendarDayMap,
  EventDescription,
} from '~/composables/types/event'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { eventDateGetters } from '~/utils/eventDateGetters'

const { locale } = useI18n()

const eventBuyBoxVisibleElements = {
  giftcard: true,
  ...(inject('eventBuyBoxVisibleElements') as {
    calendar?: boolean
    requestButtons?: boolean
    giftcard?: boolean
  }),
}

const {
  textColor,
  backgroundColor,
  cardBackgroundColor = 'transparent',
  isEmbedded = false,
} = defineProps<{
  textColor?: string
  backgroundColor?: string
  cardBackgroundColor?: string
  isEmbedded?: boolean
}>()

const emit = defineEmits<{
  (
    e:
      | 'dates-loaded'
      | 'pricings-loaded'
      | 'toggle-date-picker'
      | 'add-item-to-cart'
  ): void
}>()

const eventData = inject<EventDescription | undefined>('eventData')
const isSpecialGiftcardEvent = inject('isSpecialGiftcardEvent') as boolean

// Guard against missing eventData - component will not render without it
if (!eventData && import.meta.dev) {
  console.warn('EventDetailsSidebar: eventData not provided via inject')
}
const router = useRouter()
const isKitsPage = computed(
  () =>
    router.currentRoute.value.name?.toString().split('___')[0] ===
      'k-boxPermalink' ||
    (isEmbedded && eventData && eventGetters.isBoxEvent(eventData))
)

const isSubdomain = useIsSubdomain()
const calendarDates = ref<EventCalendarDayMap | Record<any, any> | undefined>(
  undefined
)

const isBuyBoxReady = ref(false)

const firstAvailableDate = computed(() => {
  if (!calendarDates.value) return undefined
  return eventDateGetters.getFirstAvailableDate(calendarDates.value)
})

const firstAvailableMonth = computed(() => {
  if (!calendarDates.value) return undefined
  return calendarDates.value
})

const areThereDatesAvailable = computed(() =>
  eventDateGetters.areThereDatesAvailable(calendarDates.value)
)

const availableDatesQuantity = computed(
  () => Object.keys(calendarDates.value ?? {}).length || 0
)

const shouldEnableGiftcard = computed(
  () =>
    eventData &&
    eventBuyBoxVisibleElements.giftcard &&
    !eventGetters.getIsTeamEvent(eventData) &&
    (!isSubdomain.value ||
      supplierGetters.getHasGiftcardEmbedded(
        eventGetters.getSupplier(eventData)
      ))
)

const shouldDisplayEventBuybox = computed(() => {
  return !isSpecialGiftcardEvent && areThereDatesAvailable.value
})

const isKitsAvailable = computed(() => {
  return (
    isKitsPage.value &&
    areThereDatesAvailable.value &&
    calendarDates.value?.[0]?.tickets_left > 0
  )
})

const shouldDisplayTeamEventRequestCard = computed(
  () =>
    eventData &&
    !isEmbedded &&
    !isSpecialGiftcardEvent &&
    !eventGetters.isBoxEvent(eventData)
)

const shouldDisplayCorporateGiftingCard = computed(
  () =>
    eventData &&
    !isEmbedded &&
    (isSpecialGiftcardEvent || eventGetters.isBoxEvent(eventData))
)

const teamEventRequestPrice = computed(() => {
  if (!eventData) return false
  if (eventGetters.getNextDateAvailable(eventData)) return false
  const defaultPriceAmount = eventGetters.getDefaultPriceAmount(eventData)

  if (defaultPriceAmount === 0) return false

  return formatSmartPriceAmount(Number(defaultPriceAmount), locale.value)
})

const config = useRuntimeConfig()
const { apiBaseURL } = config.public

onBeforeMount(async () => {
  await nextTick()
  if (isSpecialGiftcardEvent) {
    isBuyBoxReady.value = true
    return
  }

  // Guard: cannot fetch dates without eventData
  if (!eventData) {
    if (import.meta.dev) {
      console.error(
        'EventDetailsSidebar: Cannot fetch calendar dates without eventData'
      )
    }
    isBuyBoxReady.value = true
    return
  }

  try {
    calendarDates.value = await $fetch(
      `/v1/store/events/${eventGetters.getId(eventData)}/calendar`,
      {
        method: 'GET',
        baseURL: apiBaseURL,
      }
    )

    emit('dates-loaded')
  } catch (error) {
    console.error('Failed to fetch calendar dates:', error)
    calendarDates.value = undefined
  } finally {
    isBuyBoxReady.value = true
  }
})
</script>

<style lang="postcss" scoped>
@import '~/assets/css/loading.css';

.preloader {
  @apply min-h-[300px] rounded-lg;
}
</style>
